'use client';
import {
	type Country,
	CountrySchema,
	useCountries,
	useIntl,
	type UserRequirement,
	type UserTurkishExtraFieldsUserProvidedValues
} from '@capsicum/common';
import { useMe } from '@capsicum/socios-core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useGetRequirementStateByIdentifierQuery, useSubmitRequirementStateByIdentifierQuery } from '../data-access';

type TurkishExtraFieldsFormType = z.infer<ReturnType<typeof useTurkishExtraFieldsSchema>>;

const useTurkishExtraFieldsSchema = () => {
	const { $t } = useIntl();

	return z.object({
		city: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('requirements.common.form.city.label'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				255,
				$t('requirements.common.form.errors.length', {
					field: $t('requirements.common.form.city.label'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			),
		cityOfBirth: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('requirements.common.form.city.label'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				255,
				$t('requirements.common.form.errors.length', {
					field: $t('requirements.common.form.city.label'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			),
		country: CountrySchema,
		countryOfBirth: CountrySchema,
		parent1FullName: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('turkishExtraFields.form.parent1.placeholder'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				255,
				$t('requirements.common.form.errors.length', {
					field: $t('turkishExtraFields.form.parent1.placeholder'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			),
		parent2FullName: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('turkishExtraFields.form.parent1.placeholder'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				255,
				$t('requirements.common.form.errors.length', {
					field: $t('turkishExtraFields.form.parent1.placeholder'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			),
		region: z.preprocess(
			val => (val === '' ? undefined : val),
			z
				.string()
				.min(
					2,
					$t('requirements.common.form.errors.length', {
						field: $t('requirements.common.form.region.labelShort'),
						max: 255,
						min: 2,
						value: $t('requirements.common.form.errors.short')
					})
				)
				.max(
					255,
					$t('requirements.common.form.errors.length', {
						field: $t('requirements.common.form.region.labelShort'),
						max: 255,
						min: 2,
						value: $t('requirements.common.form.errors.long')
					})
				)
				.optional()
				.nullable()
		),
		street: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('requirements.common.form.street.label'),
					max: 512,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				512,
				$t('requirements.common.form.errors.length', {
					field: $t('requirements.common.form.street.label'),
					max: 512,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			),
		zipCode: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('requirements.common.form.postcode.label'),
					max: 50,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				50,
				$t('requirements.common.form.errors.length', {
					field: $t('requirements.common.form.postcode.label'),
					max: 50,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			)
	});
};

const useTurkishExtraFieldsForm = () => {
	const { me } = useMe();
	const { countries } = useCountries();

	const userAddressCountry = useMemo(
		() => countries.find((country: Country) => country.iso2 === me.address?.countryCode),
		[countries, me.address?.countryCode]
	);

	const turkishExtraFieldsForm = useForm<TurkishExtraFieldsFormType>({
		defaultValues: useMemo(
			() => ({
				city: me.address?.city || '',
				cityOfBirth: '',
				country: userAddressCountry,
				parent1FullName: '',
				parent2FullName: '',
				region: me.address?.region || null,
				street: me.address?.street || '',
				zipCode: me.address?.zipCode || ''
			}),
			[me.address?.city, me.address?.region, me.address?.street, me.address?.zipCode, userAddressCountry]
		),
		delayError: 200,
		mode: 'onChange',
		resolver: zodResolver(useTurkishExtraFieldsSchema())
	});

	// Resetting the address when we get users' address
	useEffect(() => {
		if (me.address && userAddressCountry)
			turkishExtraFieldsForm.reset({
				city: me.address?.city,
				country: userAddressCountry,
				region: me.address?.region,
				street: me.address?.street,
				zipCode: me.address?.zipCode
			});
	}, [
		turkishExtraFieldsForm,
		me.address?.street,
		me.address?.city,
		me.address?.region,
		me.address?.zipCode,
		me.address,
		userAddressCountry
	]);

	return turkishExtraFieldsForm;
};

type UseTurkishExtraFieldsProps = {
	requirement: UserRequirement;
};

export const useTurkishExtraFields = ({ requirement }: UseTurkishExtraFieldsProps) => {
	const { data: requirementState, isFetching: isFetchingPOAStatus } = useGetRequirementStateByIdentifierQuery({
		requirementIdentifier: 'proof_of_address'
	});
	const { isLoading: areCountriesLoading } = useCountries();
	const { isLoading, isSuccess, mutate } = useSubmitRequirementStateByIdentifierQuery();

	const canUpdateAddress = useMemo(() => {
		return requirementState?.status !== 'completed_allow';
	}, [requirementState?.status]);

	const isFormLoading = isFetchingPOAStatus || areCountriesLoading;

	const turkishExtraFieldsForm = useTurkishExtraFieldsForm();

	const handleSubmitForm = turkishExtraFieldsForm.handleSubmit(data => {
		const payload: UserTurkishExtraFieldsUserProvidedValues = {
			birthPlaceCity: data.cityOfBirth,
			birthPlaceCountry: data.countryOfBirth.iso2,
			cityAddress: data.city,
			countryCodeAddress: data.country.iso2,
			nameParentOne: data.parent1FullName,
			nameParentTwo: data.parent2FullName,
			regionAddress: data.region || null,
			streetAddress: data.street,
			zipCodeAddress: data.zipCode
		};

		mutate({ requirementData: payload, requirementIdentifier: requirement.identifier });
	});

	return {
		canUpdateAddress,
		handleSubmitForm,
		isFormLoading,
		isSubmitLoading: isLoading || isSuccess,
		turkishExtraFieldsForm
	};
};
